import { SaveTemplateVersionBody } from '@loyalty/shared-common/template-api-client';
import { useAlerts } from '@qualifio/ui-commons';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../shared/api-client/ApiClientProvider';
import { useEditorStore } from '../store';
import { useProgram } from '../../loyalty/ProgramContext';

const PAGE_SIZE = 50;

export function useTemplates() {
  const { templateApi } = useApi();
  const { program } = useProgram();
  const templatesQuery = useInfiniteQuery({
    queryKey: ['templates', program.id],
    queryFn: ({ pageParam }) =>
      templateApi.getLastTemplates(program.id, {
        params: { size: PAGE_SIZE, skip: pageParam * PAGE_SIZE },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) {
        return undefined;
      }
      return allPages.length;
    },
  });

  return {
    ...templatesQuery,
    templates: templatesQuery.data?.pages.flat() ?? [],
  };
}

export function useTemplateById(templateId?: string | null) {
  const { templateApi } = useApi();
  return useQuery({
    queryKey: ['template', templateId],
    queryFn: () => templateApi.getTemplateById(templateId!),
    enabled: !!templateId,
  });
}

export function useCurrentTemplate() {
  const currentTemplateId = useEditorStore(state => state.currentTemplateId);
  return useTemplateById(currentTemplateId);
}

export function useSaveTemplate() {
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { templateApi } = useApi();
  const { program } = useProgram();
  const setCurrentTemplateId = useEditorStore(state => state.setCurrentTemplateId);

  return useMutation({
    mutationFn: async (template: SaveTemplateVersionBody) => {
      return await templateApi.saveTemplate(program.id, template);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['templates', program.id],
      });
      setCurrentTemplateId(data.id || null);
      notify(t('editor.custom-fonts.success.save-template'), 'success');
    },
    onError: error => {
      notify(t('editor.custom-fonts.errors.save-template', { error }), 'error');
    },
  });
}

export function useUpdateTemplateName() {
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { templateApi } = useApi();
  const { program } = useProgram();
  return useMutation({
    mutationFn: ({
      templateId,
      name,
      description,
    }: {
      templateId: string;
      name: string;
      description?: string;
    }) =>
      templateApi.updateTemplateName(templateId, {
        name,
        description: description || '',
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['template', data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['templates', program.id],
      });
      notify(t('editor.template.version.name.success'), 'success');
    },
    onError: error => {
      notify(t('editor.template.version.name.error', { error }), 'error');
    },
  });
}

export function usePublishedTemplate() {
  const { program } = useProgram();
  return useTemplateById(program.templateId);
}

export function usePublishTemplate() {
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const { gamificationApi } = useApi();
  const { program, setProgram } = useProgram();
  return useMutation({
    mutationFn: (templateId: string) => gamificationApi.publishTemplate(program.id, templateId),
    onSuccess: data => {
      setProgram({ ...program, templateId: data.templateId });
      notify(t('editor.custom-fonts.success.publish-template'), 'success');
    },
    onError: error => {
      notify(t('editor.custom-fonts.errors.publish-template', { error }), 'error');
    },
  });
}

export function useScheduledTemplate() {
  const { program } = useProgram();
  const { templateApi } = useApi();
  return useQuery({
    queryKey: ['scheduledTemplate', program.id],
    queryFn: () => templateApi.getScheduledTemplate(program.id),
  });
}

export function useScheduleTemplate() {
  const { templateApi } = useApi();
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { program } = useProgram();
  return useMutation({
    mutationFn: ({ templateId, publicationDate }: { templateId: string; publicationDate: Date }) =>
      templateApi.scheduleTemplate(program.id, templateId, publicationDate.toISOString()),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['template', data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['scheduledTemplate', program.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['templates', program.id],
      });
      notify(t('editor.template.version.schedule.success'), 'success');
    },
    onError: error => {
      notify(t('editor.template.version.schedule.error', { error }), 'error');
    },
  });
}

export function useCancelScheduleTemplate() {
  const { templateApi } = useApi();
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { program } = useProgram();
  return useMutation({
    mutationFn: () => templateApi.cancelScheduleTemplate(program.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['scheduledTemplate', program.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['templates', program.id],
      });
      notify(t('editor.template.version.schedule.cancel.success'), 'success');
    },
    onError: error => {
      notify(t('editor.template.version.schedule.cancel.error', { error }), 'error');
    },
  });
}
